"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "@/lib/theme/them-provider";
import { useLocale } from "@/lib/locale/locale-provider";
import type { GiftItem, GiftStatus } from "@/lib/sections/gifts.types";
import { MotionButton } from "@/components/animations/FadeIn";
import { GiftParticipantsList } from "@/components/gifts/GiftParticipantsList";

type Props = {
  gift: GiftItem;
  status: GiftStatus;
  currency: string;
  onClose: () => void;
  onAddParticipant: (p: {
    name: string;
    amount?: number;
    note?: string;
  }) => Promise<
    | {
        message?: string;
        redirectHref?: string;
      }
    | string
    | void
  >;
};

const inputStyles = {
  width: "100%",
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.10)",
  padding: "12px 14px",
  outline: "none",
  transition: "border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease",
} as const;

const NAVBAR_CLEARANCE_PX = 104;
const MODAL_GUTTER_PX = 16;

export function GiftSelectionModal({
  gift,
  status,
  currency,
  onClose,
  onAddParticipant,
}: Props) {
  const { theme } = useTheme();
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const [name, setName] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const copy = useMemo(() => {
    if (locale === "fr") {
      return {
        dialogLabel: "Sélection de cadeau",
        close: "Fermer",
        openGift: "Ouvrir le cadeau",
        reserved: "Cette idée est déjà complète, mais vous pouvez toujours la consulter.",
        partial: "Cette idée a déjà reçu une première contribution.",
        free: "Cette idée est encore disponible.",
        slotsHelper: (count: number) => `Vous pouvez vous inscrire jusqu'à ${count} personne(s).`,
        targetHelper: (value: number) =>
          `Montant indicatif : ${value} ${currency}. Vous pouvez contribuer librement.`,
        generalHelper: "Vous pouvez contribuer quand vous le souhaitez.",
        existingSummary: (count: number) =>
          count > 0 ? `${count} contribution(s) déjà enregistrée(s).` : "Aucune contribution enregistrée pour le moment.",
        formTitle: "Contribuer",
        closedTitle: "Contributions fermées",
        name: "Votre nom",
        amount: `Montant (optionnel) - par ex. 50 ${currency}`,
        note: "Message (optionnel)",
        submit: "Envoyer",
        submitting: "Envoi...",
        closedHint: "Cette idée ne peut plus recevoir de nouvelle contribution.",
        invalidAmount: "Veuillez saisir un montant valide.",
        thankYou: "Merci, votre contribution a bien été enregistrée.",
        footer: "Merci de faire partie de cette journée.",
      };
    }

    if (locale === "en") {
      return {
        dialogLabel: "Gift selection",
        close: "Close",
        openGift: "Open gift",
        reserved: "This gift is already complete, but you can still view it.",
        partial: "This gift already has a contribution.",
        free: "This gift is still available.",
        slotsHelper: (count: number) => `Up to ${count} guest(s) can sign up.`,
        targetHelper: (value: number) =>
          `Suggested amount: ${value} ${currency}. You can contribute any amount you like.`,
        generalHelper: "You can contribute whenever you like.",
        existingSummary: (count: number) =>
          count > 0 ? `${count} contribution(s) already saved.` : "No contributions saved yet.",
        formTitle: "Contribute",
        closedTitle: "Contributions closed",
        name: "Your name",
        amount: `Amount (optional) - e.g. 50 ${currency}`,
        note: "Message (optional)",
        submit: "Submit",
        submitting: "Submitting...",
        closedHint: "This gift cannot accept more contributions right now.",
        invalidAmount: "Please enter a valid amount.",
        thankYou: "Thank you, your contribution was saved successfully.",
        footer: "Thank you for being part of our day.",
      };
    }

    return {
      dialogLabel: "Geschenkauswahl",
      close: "Schliessen",
      openGift: "Geschenk ansehen",
      reserved: "Dieses Geschenk ist bereits vollstaendig bedacht, ihr koennt es euch aber weiterhin ansehen.",
      partial: "Dieses Geschenk hat bereits erste Beitraege erhalten.",
      free: "Dieses Geschenk ist noch offen.",
      slotsHelper: (count: number) => `Ihr koennt euch mit bis zu ${count} Person(en) eintragen.`,
      targetHelper: (value: number) =>
        `Richtwert: ${value} ${currency}. Ihr koennt jeden beliebigen Betrag beisteuern.`,
      generalHelper: "Ihr koennt euch jederzeit beteiligen.",
      existingSummary: (count: number) =>
        count > 0 ? `${count} Beitrag/Beitraege sind bereits eingegangen.` : "Noch keine Beitraege eingetragen.",
      formTitle: "Beitrag senden",
      closedTitle: "Beitrag geschlossen",
      name: "Dein Name",
      amount: `Betrag (optional) - z.B. 50 ${currency}`,
      note: "Nachricht (optional)",
      submit: "Eintragen",
      submitting: "Wird gespeichert...",
      closedHint: "Dieses Geschenk kann aktuell nicht weiter ergaenzt werden.",
      invalidAmount: "Bitte gib einen gueltigen Betrag ein.",
      thankYou: "Danke, dein Beitrag wurde erfolgreich gespeichert.",
      footer: "Danke, dass ihr Teil unseres Tages seid.",
    };
  }, [currency, locale]);

  const isReserved = status === "reserved";
  const participantCount = gift.contributionCount ?? gift.participants.length;
  const contributedAmount =
    typeof gift.totalAmount === "number"
      ? gift.totalAmount
      : gift.participants.reduce((sum, participant) => sum + (participant.amount ?? 0), 0);

  const canJoin =
    status !== "reserved" &&
    (typeof gift.targetSlots === "number" ? participantCount < gift.targetSlots : true) &&
    (typeof gift.targetAmount === "number"
      ? contributedAmount < gift.targetAmount
      : true);

  const showAmount = typeof gift.targetAmount === "number" || typeof gift.suggestedAmount === "number";

  const helper = useMemo(() => {
    if (typeof gift.targetSlots === "number") {
      return copy.slotsHelper(gift.targetSlots);
    }
    if (typeof gift.targetAmount === "number") {
      return copy.targetHelper(gift.targetAmount);
    }
    if (typeof gift.suggestedAmount === "number") {
      return copy.targetHelper(gift.suggestedAmount);
    }
    return copy.generalHelper;
  }, [copy, currency, gift.suggestedAmount, gift.targetAmount, gift.targetSlots]);

  const statusText = useMemo(() => {
    if (status === "reserved") return copy.reserved;
    if (status === "partial") return copy.partial;
    return copy.free;
  }, [copy, status]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFirstElement = () => {
      closeButtonRef.current?.focus();
    };

    const timer = window.setTimeout(focusFirstElement, 40);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusableElements || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmed = name.trim();
    if (!trimmed || isSubmitting || !canJoin) return;

    setErrorMessage(null);
    setSuccessMessage(null);

    const normalizedAmount = amount.trim().replace(",", ".");
    const parsedAmount = showAmount && normalizedAmount ? Number(normalizedAmount) : undefined;
    const hasInvalidAmount =
      showAmount &&
      normalizedAmount &&
      (typeof parsedAmount !== "number" || !Number.isFinite(parsedAmount) || parsedAmount <= 0);

    if (hasInvalidAmount) {
      setErrorMessage(copy.invalidAmount);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await onAddParticipant({
        name: trimmed,
        amount: typeof parsedAmount === "number" ? parsedAmount : undefined,
        note: note.trim() || undefined,
      });
      const message =
        typeof result === "string" ? result : result?.message ?? copy.thankYou;
      const redirectHref = typeof result === "string" ? undefined : result?.redirectHref;

      setSuccessMessage(message);
      setName("");
      setAmount("");
      setNote("");

      if (redirectHref && typeof window !== "undefined") {
        window.setTimeout(() => {
          window.location.assign(redirectHref);
        }, 120);
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(copy.invalidAmount);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={copy.dialogLabel}
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={prefersReducedMotion ? undefined : { opacity: 1 }}
      exit={prefersReducedMotion ? undefined : { opacity: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: `${NAVBAR_CLEARANCE_PX}px ${MODAL_GUTTER_PX}px ${MODAL_GUTTER_PX}px`,
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <motion.div
        ref={dialogRef}
        initial={prefersReducedMotion ? false : { opacity: 0, y: 28, scale: 0.985 }}
        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
        exit={prefersReducedMotion ? undefined : { opacity: 0, y: 18, scale: 0.985 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: `calc(100dvh - ${NAVBAR_CLEARANCE_PX + MODAL_GUTTER_PX * 2}px)`,
          background: theme.colors.surface,
          borderRadius: 22,
          boxShadow: "0 30px 80px rgba(0,0,0,0.18)",
          overflowX: "hidden",
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            padding: 18,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            background: theme.colors.surface,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontFamily: theme.fonts.heading, color: theme.colors.text }}>
                {gift.title}
              </h3>
              <p style={{ margin: "6px 0 0", color: theme.colors.textMuted, lineHeight: 1.6 }}>
                {statusText}{" "}
                <span style={{ color: theme.colors.primary, fontWeight: 600 }}>{helper}</span>
              </p>
              <p style={{ margin: "8px 0 0", color: theme.colors.textMuted, fontSize: 13 }}>
                {copy.existingSummary(gift.contributionCount ?? gift.participants.length)}
              </p>
            </div>

            <MotionButton
              ref={closeButtonRef}
              onClick={onClose}
              aria-label={copy.close}
              style={{
                border: "none",
                background: "white",
                borderRadius: 12,
                padding: "8px 10px",
                cursor: "pointer",
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
              }}
            >
              x
            </MotionButton>
          </div>

          {gift.link?.href ? (
            <a
              href={gift.link.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: 10,
                color: theme.colors.primary,
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              {gift.link.label ?? copy.openGift} {"->"}
            </a>
          ) : null}
        </div>

        <form onSubmit={submit} style={{ padding: 18, display: "grid", gap: 14 }}>
          <GiftParticipantsList participants={gift.participants} currency={currency} />

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "white",
              boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
            }}
          >
            <p style={{ margin: "0 0 10px", color: theme.colors.text, fontWeight: 700 }}>
              {isReserved ? copy.closedTitle : copy.formTitle}
            </p>

            <div style={{ display: "grid", gap: 10 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                  {copy.name}
                </span>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={!canJoin || isSubmitting}
                  style={inputStyles}
                />
              </label>

              {showAmount ? (
                <label style={{ display: "grid", gap: 6 }}>
                  <span style={{ color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                    {copy.amount}
                  </span>
                  <input
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    disabled={!canJoin || isSubmitting}
                    inputMode="decimal"
                    style={inputStyles}
                  />
                </label>
              ) : null}

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: theme.colors.text, fontSize: 14, fontWeight: 600 }}>
                  {copy.note}
                </span>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  disabled={!canJoin || isSubmitting}
                  rows={3}
                  style={{ ...inputStyles, resize: "vertical" }}
                />
              </label>

              {errorMessage ? (
                <p role="alert" style={{ margin: 0, color: "#c2410c", fontSize: 13, lineHeight: 1.5 }}>
                  {errorMessage}
                </p>
              ) : null}

              {successMessage ? (
                <p
                  role="status"
                  style={{ margin: 0, color: theme.colors.primary, fontSize: 13, fontWeight: 700, lineHeight: 1.5 }}
                >
                  {successMessage}
                </p>
              ) : null}

              <MotionButton
                type="submit"
                disabled={!canJoin || !name.trim()}
                style={{
                  border: "none",
                  borderRadius: 14,
                  padding: "12px 14px",
                  cursor: !canJoin || !name.trim() || isSubmitting ? "not-allowed" : "pointer",
                  background:
                    !canJoin || !name.trim() || isSubmitting ? "rgba(0,0,0,0.10)" : theme.colors.primary,
                  color: !canJoin || !name.trim() || isSubmitting ? theme.colors.textMuted : "white",
                  fontWeight: 800,
                }}
              >
                {isSubmitting ? copy.submitting : copy.submit}
              </MotionButton>

              {!canJoin && !isReserved ? (
                <p style={{ margin: 0, color: theme.colors.textMuted, fontSize: 13 }}>
                  {copy.closedHint}
                </p>
              ) : null}
            </div>
          </div>

          <p style={{ margin: 0, color: theme.colors.textMuted, fontSize: 13, lineHeight: 1.6 }}>
            {copy.footer}
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
}
